"use client";

import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Loader2, Zap } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { CreatedTaskPill } from "@/components/ai-elements/created-task-pill";
import { WorkflowApiError } from "@/api/workflows";
import { composerRun, trackComposerRun, useComposerRunRevision } from "@/lib/composer-action-runs";

export function ComposerActionMessageCard({ label, taskId, actionId, sessionId, fetchWithAuth, onOpenRun }: {
  label: string;
  taskId: string;
  actionId?: string;
  sessionId: string;
  fetchWithAuth: (url: string, init: RequestInit) => Promise<Response>;
  onOpenRun?: (taskId: string) => void;
}) {
  const { t } = useTranslation("chat");
  useComposerRunRevision();
  useEffect(() => {
    trackComposerRun(taskId, actionId ?? "", sessionId, async () => {
      const response = await fetchWithAuth(`/api/workflows/${encodeURIComponent(taskId)}/status`, { headers: {}, cache: "no-store" });
      if (!response.ok) throw new WorkflowApiError("Workflow status unavailable", response.status);
      const payload: unknown = await response.json();
      if (!payload || typeof payload !== "object" || !("status" in payload) || typeof payload.status !== "string") {
        throw new WorkflowApiError("Invalid workflow status", response.status);
      }
      return payload.status;
    });
  }, [taskId, actionId, sessionId, fetchWithAuth]);
  const statusKey = composerRun(taskId)?.status ?? "running";
  const validTaskId = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(taskId);
  return <section className="flex flex-wrap items-center gap-2 rounded-md border bg-muted/40 px-3 py-2 text-sm" aria-label={t("composerActions.transcriptLabel")}>
    <Zap className="size-4" aria-hidden="true" />
    <span>{t("composerActions.startedNoId", { label })}</span>
    <Badge variant="secondary" className="gap-1">
      {statusKey === "running" && <Loader2 className="size-3 animate-spin" aria-hidden="true" />}
      {t(`composerActions.status.${statusKey}`)}
    </Badge>
    <CreatedTaskPill kind="workflow-run" taskId={validTaskId ? taskId : null}
      onClick={validTaskId && onOpenRun ? () => onOpenRun(taskId) : undefined} />
  </section>;
}
